import type { RecognizedDocument } from "@/api/schemas";
import { DOC_TYPE_META } from "./OpciDioDocsPanel";

/**
 * Donesi konzola (F1) — pure row logic, no React.
 *
 * One rule for the whole console: every row = one item = one place for
 * input, status and evidence. This module derives the rows' expected
 * document types (the ``expected_type`` hint a targeted drop sends to
 * ``/api/recognize-document``), pairs recognized documents to rows, and
 * detects hint/classification conflicts so the row can say "ovo izgleda
 * kao X — potvrdi ili ispravi" instead of silently mis-filing.
 */

// ---------------------------------------------------------------------------
// ① PROJEKT — the required form data, in the architect's mental units
// (mirrors the required set in schema.ts / DocumentGenerationRequest).
// ``inline`` rows open a mini-entry on the row itself; the rest link to
// Provjeri (too many fields / a select driven by the zahvat definition).
// ---------------------------------------------------------------------------

export interface ProjektField {
  name: string;
  label: string;
  type: "text" | "date" | "number";
  placeholder?: string;
}

export interface ProjektRowDef {
  label: string;
  fields: ProjektField[];
  /** Row opens an inline mini-entry (2–3 fields). */
  inline: boolean;
}

export const PROJEKT_ROWS: ProjektRowDef[] = [
  {
    label: "Broj, datum i lokacija projekta",
    inline: true,
    fields: [
      { name: "project_number", label: "Broj projekta", type: "text", placeholder: "npr. 07/2026" },
      { name: "project_date", label: "Datum", type: "date" },
      { name: "project_location", label: "Mjesto izrade", type: "text", placeholder: "npr. Rijeka" },
    ],
  },
  {
    label: "Investitor (ime, adresa, OIB)",
    inline: true,
    fields: [
      { name: "investitor_name", label: "Ime / naziv", type: "text" },
      { name: "investitor_address", label: "Adresa", type: "text" },
      { name: "investitor_oib", label: "OIB", type: "text" },
    ],
  },
  {
    label: "Ured i glavni projektant",
    inline: false, // 5 fields — the Provjeri form is the right place
    fields: [
      { name: "ured_name", label: "Naziv ureda", type: "text" },
      { name: "ured_address", label: "Adresa ureda", type: "text" },
      { name: "glavni_projektant_name", label: "Glavni projektant", type: "text" },
      { name: "glavni_projektant_license", label: "Broj ovlaštenja", type: "text" },
      { name: "direktor_name", label: "Direktor", type: "text" },
    ],
  },
  {
    label: "Građevna čestica (k.č., k.o., površina)",
    inline: true,
    fields: [
      { name: "katastarska_cestica", label: "k.č.br.", type: "text", placeholder: "npr. 706/1" },
      { name: "katastarski_opcina", label: "k.o.", type: "text" },
      { name: "cestica_povrsina_m2", label: "Površina (m²)", type: "number" },
    ],
  },
  {
    label: "Vrsta građevine",
    inline: false, // comes from the zahvat definition, not free text
    fields: [{ name: "building_type", label: "Vrsta građevine", type: "text" }],
  },
];

/** Filled-state of one Projekt row against the draft. ``building_type`` may
 * also live in the feasibility params (the fallback). */
export function projektRowState(
  row: ProjektRowDef,
  projectData: Record<string, unknown>,
  buildingTypeFallback?: string | null,
): { present: boolean; missing: ProjektField[] } {
  const filled = (f: ProjektField) => {
    if (f.name === "building_type") {
      return Boolean(projectData[f.name] ?? buildingTypeFallback);
    }
    const v = projectData[f.name];
    return v != null && v !== "" && v !== 0;
  };
  const missing = row.fields.filter((f) => !filled(f));
  return { present: missing.length === 0, missing };
}

export interface ProjektProgress {
  filled: number;
  total: number;
  /** Missing required fields, in PROJEKT_ROWS order (= console order). */
  missing: ProjektField[];
}

/**
 * F2 — progress of the REQUIRED form data over all Projekt rows, for the
 * "X od N popunjeno · fali: …" header on the Provjeri form. Same source of
 * truth as the console's ① Projekt group (``PROJEKT_ROWS`` +
 * ``projektRowState``), so step 1 and step 2 can never disagree about what
 * counts as "fali".
 */
export function projektProgress(
  projectData: Record<string, unknown>,
  buildingTypeFallback?: string | null,
): ProjektProgress {
  const missing: ProjektField[] = [];
  let total = 0;
  for (const row of PROJEKT_ROWS) {
    total += row.fields.length;
    missing.push(...projektRowState(row, projectData, buildingTypeFallback).missing);
  }
  return { filled: total - missing.length, total, missing };
}

// ---------------------------------------------------------------------------
// ③ TEKST / ④ NACRTI — document rows with a fixed definition (the opći-dio
// rows come from /api/opci-dio/status instead, see expectedTypesForSlot).
// ``types``   — recognized document_types that satisfy the row (evidence);
// ``expectedType`` — the hint a targeted drop sends to the backend.
// ---------------------------------------------------------------------------

export interface KonzolaDocRowDef {
  key: string;
  label: string;
  types: string[];
  expectedType: string;
}

export const GEODETSKI_ROW: KonzolaDocRowDef = {
  key: "geodetski_elaborat",
  label: "Geodetski elaborat",
  types: ["geodetski_elaborat"],
  expectedType: "geodetski_elaborat",
};

export const NACRTI_ROWS: KonzolaDocRowDef[] = [
  {
    key: "situacija",
    label: "Situacija",
    // The situacija figures can arrive on any of these sheet types (R3).
    types: ["situacija_sira", "situacija_uza", "geodetski_elaborat"],
    expectedType: "situacija_uza",
  },
  { key: "tlocrti", label: "Tlocrti", types: ["tlocrt"], expectedType: "tlocrt" },
  { key: "presjeci", label: "Presjeci", types: ["presjek"], expectedType: "presjek" },
  { key: "procelja", label: "Pročelja", types: ["procelje"], expectedType: "procelje" },
];

// ---------------------------------------------------------------------------
// ② OPĆI DIO — slot rows from /api/opci-dio/status.
// ---------------------------------------------------------------------------

/** Reverse of ``DOC_TYPE_META``: document types whose home is this slot.
 * (Slot 1.10 legitimately has two — posebni uvjeti + poziv.) */
export function expectedTypesForSlot(slot: string): string[] {
  return Object.entries(DOC_TYPE_META)
    .filter(([, m]) => m.slot === slot)
    .map(([k]) => k);
}

/** The hint a drop on a slot row sends — the slot's primary type. */
export function expectedTypeForSlot(slot: string): string | undefined {
  return expectedTypesForSlot(slot)[0];
}

/** Home slot of a recognized document (override-aware). */
export function slotOfDoc(doc: RecognizedDocument): string | null {
  return doc.target_slot ?? DOC_TYPE_META[doc.document_type]?.slot ?? null;
}

// ---------------------------------------------------------------------------
// Pairing documents to rows + conflict detection.
// ---------------------------------------------------------------------------

export interface IndexedDoc {
  doc: RecognizedDocument;
  /** Index in ``intake.recognizedDocs`` — the store's override/remove key. */
  index: number;
}

export function docsOfTypes(
  docs: RecognizedDocument[],
  types: string[],
): IndexedDoc[] {
  const set = new Set(types);
  return docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => set.has(doc.document_type));
}

export function docsOfSlot(docs: RecognizedDocument[], slot: string): IndexedDoc[] {
  return docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => slotOfDoc(doc) === slot);
}

export interface RowMismatch {
  doc: RecognizedDocument;
  index: number;
  /** What the row expected (the hint sent with the upload). */
  expected: string;
  /** What the classifier decided instead. */
  got: string;
}

/**
 * Conflicts to show ON the row that initiated a targeted upload.
 *
 * A document carries ``_expected_mismatch`` when the backend classified it
 * differently from the row's hint. It belongs to THIS row when its
 * ``_expected_type`` is one the row sends/accepts. Family tolerance: if the
 * final type still satisfies the row (šira situacija dropped on the
 * Situacija row), that's not a user-facing conflict. A manual override
 * (``_user_overridden``) resolves the question — message gone.
 */
export function mismatchesForRow(
  docs: RecognizedDocument[],
  rowExpected: string[],
  rowAccepted: string[] = rowExpected,
): RowMismatch[] {
  const expectedSet = new Set(rowExpected);
  const acceptedSet = new Set(rowAccepted);
  const out: RowMismatch[] = [];
  docs.forEach((doc, index) => {
    const d = doc as Record<string, unknown>;
    const mismatch = d._expected_mismatch as
      | { expected?: string; got?: string }
      | undefined;
    if (!mismatch?.expected || !mismatch.got) return;
    if (d._user_overridden) return;
    if (!expectedSet.has(mismatch.expected)) return;
    if (acceptedSet.has(doc.document_type)) return;
    out.push({ doc, index, expected: mismatch.expected, got: mismatch.got });
  });
  return out;
}

/** Human label for a document type (falls back to the raw key). */
export function docTypeLabel(documentType: string): string {
  return DOC_TYPE_META[documentType]?.label ?? documentType;
}

/**
 * Documents no console row claims (lokacijska/građevinska dozvola,
 * nepoznato…). They stay visible in the "Ostali dokumenti" section —
 * pokaži-ili-priznaj, nothing disappears into a black hole.
 */
export function unclaimedDocs(
  docs: RecognizedDocument[],
  claimedSlots: string[],
): IndexedDoc[] {
  const slots = new Set(claimedSlots);
  const defTypes = new Set(
    [GEODETSKI_ROW, ...NACRTI_ROWS].flatMap((r) => r.types),
  );
  return docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => {
      if (defTypes.has(doc.document_type)) return false;
      const slot = slotOfDoc(doc);
      return !(slot && slots.has(slot));
    });
}
